import { useState, useEffect, useCallback, useMemo } from 'react';
import courseCategoryService, { fetchInterestData } from 'PlatForms/SelfLMS/Services/Courses/CourseCategoryService';
import { toast } from 'react-toastify';

const useCourseCategories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [interest, setInterest] = useState(null);
    const [interestLoading, setInterestLoading] = useState(true);
    const [dataInitialized, setDataInitialized] = useState(false);
    
    // Pagination state
    const [pagination, setPagination] = useState({
        currentPage: 1,
        pageSize: 10,
        totalCount: 0,
        totalPages: 0
    });

    // Memoized Main Categories - only those with subCategories
    const mainCategories = useMemo(() => {
        return categories.filter(category =>
            category.isMainCategory &&
            category.parentCategoryId === null &&
            category.subCategories?.length > 0
        );
    }, [categories]);

    // Fetch categories from API
    const fetchCategories = useCallback(async (
        onlyWithSubCategories = true, 
        page = 1, 
        pageSize = 10,
        append = false
    ) => {
        try {
            if (page === 1) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }
            
            setError(null);

            const response = await courseCategoryService.getCourseCategories(
                onlyWithSubCategories,
                page,
                pageSize
            );
            
            console.log("Categories response:", response);

            if (append) {
                // Append new categories to existing ones
                setCategories(prev => [...prev, ...(response.items || [])]);
            } else {
                // Replace categories with new ones
                setCategories(response.items || []);
            }
            
            // Update pagination state
            setPagination({
                currentPage: response.pageNumber || page,
                pageSize: response.pageSize || pageSize,
                totalCount: response.totalCount || 0,
                totalPages: response.totalPages || 0
            });

            return response.items || [];
        } catch (err) {
            setError(err.message || 'Failed to fetch categories');
            throw err;
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, []);

    // Fetch more categories
    const fetchMoreCategories = useCallback(async () => {
        if (loadingMore || pagination.currentPage >= pagination.totalPages) return;
        
        try {
            await fetchCategories(
                true, 
                pagination.currentPage + 1, 
                pagination.pageSize,
                true // append to existing categories
            );
        } catch (err) {
            console.error('Error fetching more categories:', err);
        }
    }, [fetchCategories, loadingMore, pagination]);

    // Check if there are more categories to load
    const hasMoreCategories = useMemo(() => {
        return pagination.currentPage * pagination.pageSize < pagination.totalCount;
    }, [pagination]);

    // Fetch user's existing interests
    const fetchUserInterests = useCallback(async () => {
        try {
            setInterestLoading(true);
            const data = await fetchInterestData();
            console.log("User interests:", data);
            setInterest(data);
            return data;
        } catch (err) {
            console.error('Error fetching user interests:', err);
            return null;
        } finally {
            setInterestLoading(false);
        }
    }, []);

    // Pre-select categories based on user interests
    const preSelectCategories = useCallback((categoriesData, interestsData) => {
        if (!interestsData || interestsData.length === 0 || !categoriesData || categoriesData.length === 0) {
            return [];
        }

        // Extract all subcategory IDs from the interest data
        const subCategoryIds = [];
        interestsData.forEach(category => {
            if (category.subCategories && category.subCategories.length > 0) {
                category.subCategories.forEach(sub => {
                    subCategoryIds.push(sub.id);
                });
            }
        });

        // Find the full subcategory objects from the categories list
        const preSelectedCategories = [];
        categoriesData.forEach(category => {
            if (category.subCategories && category.subCategories.length > 0) {
                category.subCategories.forEach(sub => {
                    if (subCategoryIds.includes(sub.id)) {
                        preSelectedCategories.push(sub);
                    }
                });
            }
        });

        return preSelectedCategories;
    }, []);

    // Initialize all data
    const initializeData = useCallback(async () => {
        try {
            setLoading(true);
            setInterestLoading(true);
            setDataInitialized(false);

            // Fetch categories first
            const categoriesData = await fetchCategories(true, 1, 10, false);

            // Then fetch user interests
            const interestsData = await fetchUserInterests();

            // Pre-select categories based on interests
            const preSelected = preSelectCategories(categoriesData, interestsData);
            setSelectedCategories(preSelected);

            setDataInitialized(true);
        } catch (err) {
            console.error('Error initializing data:', err);
            setError(err.message || 'Failed to initialize data');
        } finally {
            setLoading(false);
            setInterestLoading(false);
        }
    }, [fetchCategories, fetchUserInterests, preSelectCategories]);

    // Toggle category selection with validation
    const toggleCategorySelection = useCallback((category) => {
        setSelectedCategories(prev => {
            const isSelected = prev.some(item => item.id === category.id);

            // If trying to remove
            if (isSelected) {
                return prev.filter(item => item.id !== category.id);
            }
            // If trying to add - check maximum limit (11)
            else if (prev.length >= 11) {
                alert('Maximum 11 focus areas can be selected. Please remove some selections to add new ones.');
                return prev;
            }
            else {
                return [...prev, category];
            }
        });
    }, []);

    // Clear all selections
    const clearSelections = useCallback(() => {
        setSelectedCategories([]);
        setSubmitSuccess(false);
        setSubmitError(null);
    }, []);

    // Submit selections to API
    const submitSelections = useCallback(async () => {
        // Validation: Minimum 3 selections required
        if (selectedCategories.length < 3) {
            setSubmitError(`Minimum 3 focus areas required. You have selected ${selectedCategories.length}.`);
            return null;
        }

        // Get subCategoryIds from selected categories
        const subCategoryIds = selectedCategories.map(cat => cat.id);

        try {
            setIsSubmitting(true);
            setSubmitError(null);

            const response = await courseCategoryService.createInterest(subCategoryIds);
            setSubmitSuccess(true);
            console.log('Interest created successfully:', response);
            toast.success('Interest created/updated successfully:');

            // Update the interest state with the new selections
            const newInterest = mainCategories.map(category => {
                const selectedSubCategories = category.subCategories.filter(sub =>
                    subCategoryIds.includes(sub.id)
                );

                if (selectedSubCategories.length > 0) {
                    return {
                        ...category,
                        subCategories: selectedSubCategories
                    };
                }
                return null;
            }).filter(Boolean);

            setInterest(newInterest);

            return {
                success: true,
                data: response,
                selectedCount: selectedCategories.length,
                selectedCategories: selectedCategories
            };
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to save interests';
            setSubmitError(errorMessage);
            console.error('Error submitting selections:', err);
            return {
                success: false,
                error: errorMessage
            };
        } finally {
            setIsSubmitting(false);
        }
    }, [selectedCategories, mainCategories]);

    // Initialize on mount
    useEffect(() => {
        if (!dataInitialized) {
            initializeData();
        }
    }, [dataInitialized, initializeData]);

    return {
        categories,
        mainCategories,
        loading,
        loadingMore,
        error,
        selectedCategories,
        isSubmitting,
        submitError,
        submitSuccess,
        interest,
        interestLoading,
        pagination,
        hasMoreCategories,
        fetchMoreCategories,
        toggleCategorySelection,
        clearSelections,
        submitSelections,
        refetch: initializeData
    };
};

export default useCourseCategories;